import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';

export interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  subscriptionId: number;
  /** The subscriber's email, named in the title (ACT-007). */
  email: string | undefined;
  currentPeriodEnd: string | undefined;
}

export function CancelDialog(props: CancelDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <CancelForm key={useFreshOnOpen(props.open)} {...props} />;
}

function CancelForm({
  open,
  onClose,
  onDone,
  subscriptionId,
  email,
  currentPeriodEnd,
}: CancelDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const describe = useActionError();
  const api = useTenantApi();
  const [mode, setMode] = useState<'at_period_end' | 'immediate'>('at_period_end');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const until = currentPeriodEnd
    ? formatCivilDate(currentPeriodEnd.slice(0, 10), locale)
    : t('subscribers.unavailable');

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // The cancel answer carries no final charge since TMMAPI-0151 (post-paid AI retired).
      await api.cancelSubscription(subscriptionId, { mode });
      showToast(t('subscribers.toast.canceled'));
      onDone();
      onClose();
    } catch (err) {
      setError(describe(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>
          {t('subscribers.cancel.title', { email: email ?? t('subscribers.unavailable') })}
        </DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <Typography sx={{ mb: 2 }}>{t('subscribers.cancel.body', { until })}</Typography>
          <FormControl>
            <FormLabel>{t('subscribers.cancel.mode')}</FormLabel>
            <RadioGroup
              name="mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'at_period_end' | 'immediate')}
            >
              <FormControlLabel
                value="at_period_end"
                control={<Radio disabled={submitting} />}
                label={t('subscribers.cancel.atPeriodEnd')}
              />
              <FormControlLabel
                value="immediate"
                control={<Radio disabled={submitting} color="error" />}
                label={t('subscribers.cancel.immediate')}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('subscribers.cancel.keep')}
          </Button>
          <SubmitButton type="submit" color="error" loading={submitting}>
            {t('subscribers.actions.cancel')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
